import { Immutable } from 'src/interface/utility';

type ShortDurationSingularUnit =
  | 'millisecond'
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week';
type ShortDurationPluralUnit = `${ShortDurationSingularUnit}s`;
export type ShortDurationUnit =
  | ShortDurationSingularUnit
  | ShortDurationPluralUnit;

export type TimeFormat =
  | '12hr hour'
  | '12hr time'
  | '24hr time'
  | 'full 12hr time'
  | 'full 24hr time';

export type UniversalDateTimeFormat =
  | 'ISODateTiny'
  | 'ISODate'
  | 'ISOTime'
  | 'datetime-local';

export type DateTimeFormat =
  | TimeFormat
  | UniversalDateTimeFormat
  | 'short week day'
  | 'tiny date'
  | 'tiny date time'
  | 'tiny date hour'
  | 'tiny day hour'
  | 'day tiny date'
  | 'full date'
  | 'full date time'
  | 'full date time ms'
  | 'short date'
  | 'short date time'
  | 'short date time 24hr'
  | 'short date 24hr';

// If we ever need all timezones: Intl.supportedValuesOf('timeZone')
// export const pytzTimezones = ['America/New_York', 'Asia/Singapore'] as const;

export const ISO_DATE_REGEXP =
  /^(?<year>\d{4})-(?<month>[01]\d)-(?<day>[0-3]\d)$/;
export const ISO_TIME_REGEXP =
  /^(?<hour>[01]\d|2[0-3]):(?<minute>[0-5]\d)(:(?<second>[0-5]\d)(\.(?<millisecond>\d+))?)?(?<timezone>[+-](?<tzHour>[01]\d|2[0-3])(:(?<tzMinute>[0-5]\d))?|Z)?$/;
export const ISO_DATETIME_REGEXP =
  /^(?<year>\d{4})-(?<month>[01]\d)-(?<day>[0-3]\d)T(?<hour>[01]\d|2[0-3]):(?<minute>[0-5]\d)(:(?<second>[0-5]\d)(\.(?<millisecond>\d+))?)?(?<timezone>[+-](?<tzHour>[01]\d|2[0-3])(:(?<tzMinute>[0-5]\d))?|Z)?$/;

// ISO string delimiters
export const DATE_TIME_DELIMITER = 'T'; // Separates date from time: "2025-08-18T09:00:00"
export const TIMEZONE_DELIMITER = /[-+]/; // Separates time from timezone: "09:00:00-07:00"
export const TIME_PART_DELIMITER = ':'; // Separates hours:minutes:seconds
export const ISO_PARTS_DELIMITER = new RegExp(
  `[-${DATE_TIME_DELIMITER}${TIME_PART_DELIMITER}]|${TIMEZONE_DELIMITER.source}`,
); // Splits all ISO parts

export const UNIVERSAL_DATETIME_FORMATS: Immutable<{
  [Format in UniversalDateTimeFormat]: string;
}> = {
  ISODateTiny: 'yyyyMMdd',
  ISODate: 'yyyy-MM-dd',
  ISOTime: 'HH:mm:ssxxx',
  'datetime-local': "yyyy-MM-dd'T'HH:mm",
} as const;

// Ordering of `millisecondDurationOfUnit` is important for `renderDuration`
//   (requires largest unit first)
export const durationInMilliseconds: {
  [Duration in ShortDurationUnit]: number;
} = {
  week: 1_000 * 60 * 60 * 24 * 7,
  weeks: 1_000 * 60 * 60 * 24 * 7,
  day: 1_000 * 60 * 60 * 24,
  days: 1_000 * 60 * 60 * 24,
  hour: 1_000 * 60 * 60,
  hours: 1_000 * 60 * 60,
  minute: 1_000 * 60,
  minutes: 1_000 * 60,
  second: 1_000,
  seconds: 1_000,
  millisecond: 1,
  milliseconds: 1,
} as const;
